import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { forgotPassword } from "../../../data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthLayout } from "../shared/layout";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft } from "lucide-react";

export function ForgotPasswordScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const { mutate: forgotPasswordMutation, isLoading } = useMutation({
    mutationKey: "forgotPassword",
    mutationFn: forgotPassword,
    onSuccess: (response) => {
      // Server always returns success to prevent user enumeration
      const message = response?.data?.message || t("auth.resetLinkSentDesc");
      toast({
        title: t("auth.resetLinkSent"),
        description: message,
        variant: "success",
      });
      navigate("/login");
    },
    onError: (error) => {
      // Only network/server errors will reach here
      toast({
        title: t("common.error"),
        description: error?.response?.data?.message || error.message || t("auth.passwordResetFailed"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data) => {
    forgotPasswordMutation(data);
  };

  return (
    <AuthLayout 
      title={t("auth.forgotPasswordTitle")} 
      description={t("auth.forgotPasswordSubtitle")}
      backButton={
        <Button variant="link" className="px-0 font-normal" asChild>
          <Link to="/login">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")} {t("auth.signIn").toLowerCase()}
          </Link>
        </Button>
      }
    >
      <div className="grid gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t("errors.required"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("errors.invalidEmail"),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    placeholder={t("profile.emailPlaceholder")}
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>
            <Button disabled={isLoading}>
              {isLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {t("auth.sendResetLink")}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
